// assets/tmf-cart-upsell.js - TMF · personalized cart (drawer + /cart page). Rewritten 24/10/2026 for the live app.
//   1. Thumbnail: every line with a hidden _cfg gets the shopper's design drawn LIVE on its product (tmf-core,
//      same code as the product page preview) over the product photo. Loaded only when the cart has such lines.
//   2. "Edit design": link back to the product with ?cfg=<design>&tmf_line=<line key> (the personalizer refills the
//      form; Add to cart there replaces this line - assets/tmf-upsell-pdp.js).
//   3. ONE invitation in the drawer (tmf-upsell.js rules): 1 item -> matching ornament (from Dec 7: mug) when that
//      product is set up, else "shirt for a bandmate"; 2+ items -> shirt for a bandmate (name typed in the drawer,
//      thumbnail follows). "No thanks" hides that invitation for the visit.
// Line item properties: Horizon already hides keys starting with "_" (_cfg, _kind, _with, _for).
// Config = data-* on <tmf-cart-upsell-component> (sections/tmf-cart-extras.liquid).
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { ensureLive, getJson, paintDesign, kindOf, money, cartAdd } from './tmf-live.js';

const OFFER_CLASS = 'tmf-offer';
const THUMB_CLASS = 'tmf-thumb';
const EDIT_CLASS = 'tmf-edit-link';
const DISMISS_KEY = 'tmf-offer-dismissed';
const DRAWER_OPEN_EVENT = 'theme-drawer:open';
const FAM_NAME = { mug: 'mug', ornament: 'ornament', sign: 'wood sign', canvas: 'canvas', glass: 'whiskey glass', blanket: 'sherpa blanket', speaker: 'speaker', phone: 'phone case', plaque: 'plaque' };

/** @returns {Record<string, boolean>} */
function dismissed() {
  try {
    return JSON.parse(window.sessionStorage.getItem(DISMISS_KEY) || '{}') || {};
  } catch {
    return {};
  }
}
/** @param {string} type */
function dismiss(type) {
  try {
    const d = dismissed();
    d[type] = true;
    window.sessionStorage.setItem(DISMISS_KEY, JSON.stringify(d));
  } catch {
    /* private mode */
  }
}

/** @param {string} tag @param {Record<string, any>} [attrs] @param {(Node|string|null)[]} [kids] */
function el(tag, attrs = {}, kids = []) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === 'text') e.textContent = v;
    else if (k === 'cls') e.className = v;
    else e.setAttribute(k, v === true ? '' : String(v));
  }
  for (const c of kids) if (c != null) e.append(typeof c === 'string' ? document.createTextNode(c) : c);
  return e;
}

/** @param {any} item @param {RegExp} re */
function optionOf(item, re) {
  const o = (item.options_with_values || []).find((/** @type {any} */ x) => re.test(String(x.name || '')));
  return o ? String(o.value) : '';
}

/** @extends {Component} */
class TmfCartUpsellComponent extends Component {
  /** @type {any} */ #cart = null;
  /** @type {any} */ #lib = null;
  /** @type {any} */ #db = null;
  /** @type {any} */ #media = null;
  #mediaBase = '';
  /** @type {Map<string, {cv: HTMLCanvasElement, sig: string}>} thumbs by "<context>:<line key>" */
  #thumbs = new Map();
  /** @type {HTMLElement | null} */ #box = null;
  #boxSig = '';
  /** @type {MutationObserver | null} */ #observer = null;
  /** @type {Set<Element>} */ #observed = new Set();
  #frame = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */ #timer;
  #busy = false;
  #liveFailed = false;

  connectedCallback() {
    super.connectedCallback();
    this.#mediaBase = String(this.dataset.media || '').split('?')[0].replace(/[^/]*$/, '');
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DRAWER_OPEN_EVENT, this.#onOpen);
    this.#observe();
    this.refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DRAWER_OPEN_EVENT, this.#onOpen);
    this.#observer?.disconnect();
    this.#box?.remove();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#timer);
  }

  get #root() {
    return /** @type {any} */ (window).Shopify?.routes?.root || '/';
  }

  /** @param {any} event */
  #onCartUpdate = (event) => {
    const done = () => {
      this.#observe();
      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => this.refresh(), 0);
    };
    if (event?.promise) event.promise.then(done, done);
    else done();
  };

  #onOpen = () => {
    this.#observe();
    this.refresh();
  };

  #observe() {
    const targets = new Set();
    const drawer = document.getElementById('cart-drawer');
    if (drawer) targets.add(drawer);
    for (const items of document.querySelectorAll('cart-items-component')) targets.add(items.closest('.shopify-section') || items);
    let changed = false;
    for (const t of targets) if (!this.#observed.has(t)) changed = true;
    if (!changed) return;
    this.#observer?.disconnect();
    this.#observed = targets;
    this.#observer = new MutationObserver((mutations) => {
      if (mutations.every(isOwn)) return;
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#paint());
    });
    for (const t of targets) this.#observer.observe(t, { childList: true, subtree: true });
  }

  /** Lines that count as items (add-ons like gift box / note card / priority do not). */
  #physical() {
    const addonTypes = String(this.dataset.addonTypes || '')
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    return (this.#cart?.items || []).filter((/** @type {any} */ i) => !addonTypes.includes(String(i.product_type || '').toLowerCase()));
  }

  async refresh() {
    try {
      this.#cart = await (await fetch(`${this.#root}cart.js`, { headers: { Accept: 'application/json' } })).json();
    } catch {
      return;
    }
    const personalized = (this.#cart.items || []).some((/** @type {any} */ i) => i.properties && i.properties._cfg);
    if (personalized && !this.#lib && !this.#liveFailed) {
      try {
        const d = this.dataset;
        const [lib, db, media] = await Promise.all([
          ensureLive({ core: d.core || '', personalize: d.personalize || '', upsell: d.upsell || '', fonts: d.fonts || '', library: d.library || '' }),
          getJson(d.designs || ''),
          getJson(d.media || ''),
        ]);
        this.#lib = lib;
        this.#db = db;
        this.#media = media;
      } catch (error) {
        this.#liveFailed = true; // keep the product photos; no thumbnails / edit links
        console.warn('[tmf-cart] live design unavailable:', error);
      }
    }
    this.#paint();
  }

  /** @param {any} item */
  #lineData(item) {
    const raw = item?.properties?._cfg;
    if (!raw || !this.#db) return null;
    let cfg;
    try {
      cfg = JSON.parse(raw);
    } catch {
      return null;
    }
    const design = this.#db.designs?.[cfg.d];
    if (!design) return null;
    const kind = item.properties._kind || kindOf(item.product_type);
    // 26/10: shirt color is no longer a variant: line property "Color" / _cfg.color (old lines: Color option)
    const colorName = String(item.properties.Color || cfg.color || optionOf(item, /^(color|colour)$/i) || '');
    const style = optionOf(item, /^(style|type|product type|available product)$/i) || (kind === 'apparel' ? String(item.product_type || '') : '');
    const P = /** @type {any} */ (window).TMFPersonalize;
    const colorHex = kind === 'apparel' && P ? P.colorHex(colorName, []) : null;
    // 26/10: upsell lines are drawn on their own product photo (mug black/white, speaker 3W/6W from the variant)
    const U = /** @type {any} */ (window).TMFUpsell;
    const tile = kind !== 'apparel' && U?.tileKey ? U.tileKey(kind, design, cfg, item.variant_title, this.#media?.tiles) : kind;
    return { cfg, design, kind, tile, colorName, colorHex, style };
  }

  #paint() {
    if (!this.#cart) return;
    const byKey = new Map((this.#cart.items || []).map((/** @type {any} */ i) => [i.key, i]));
    const live = !!(this.#lib && this.#db);
    for (const row of /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll('tr.cart-items__table-row[data-key]'))) {
      const item = byKey.get(row.dataset.key);
      const data = live && item ? this.#lineData(item) : null;
      if (!data) continue;
      if (this.dataset.thumbs !== 'false') this.#thumb(row, item, data);
      if (this.dataset.edit !== 'false') this.#editLink(row, item, data);
    }
    if (this.dataset.offer !== 'false') this.#offer();
  }

  /** @param {HTMLElement} row @param {any} item @param {any} data */
  #thumb(row, item, data) {
    const media = row.querySelector('.cart-items__media-container') || row.querySelector('.cart-items__media');
    if (!media) return;
    const ctx = row.closest('#cart-drawer') ? 'd' : 'p';
    const mapKey = `${ctx}:${item.key}`;
    const sig = item.properties._cfg + '|' + item.variant_id;
    let entry = this.#thumbs.get(mapKey);
    if (!entry || entry.sig !== sig) {
      const cv = /** @type {HTMLCanvasElement} */ (el('canvas', { cls: THUMB_CLASS, role: 'img', 'aria-label': `Your design: ${item.properties.Name || item.product_title}` }));
      const fresh = { cv, sig };
      this.#thumbs.set(mapKey, fresh);
      const draw = () => {
        const host = cv.parentElement || media;
        const w = Math.max(60, Math.round(host.getBoundingClientRect().width || 100));
        paintDesign(cv, w, { ...data, lib: this.#lib, media: this.#media, mediaBase: this.#mediaBase, onReady: () => requestAnimationFrame(draw) });
      };
      requestAnimationFrame(draw);
      entry = fresh;
    }
    if (entry.cv.parentElement !== media) {
      media.classList.add('tmf-thumb-host');
      media.append(entry.cv);
    }
  }

  /** @param {HTMLElement} row @param {any} item @param {any} data */
  #editLink(row, item, data) {
    const details = row.querySelector('.cart-items__details');
    if (!details) return;
    let a = /** @type {HTMLAnchorElement | null} */ (details.querySelector(`.${EDIT_CLASS}`));
    if (!a) {
      a = /** @type {HTMLAnchorElement} */ (el('a', { cls: EDIT_CLASS, text: this.dataset.editText || 'Edit design' }));
      const props = details.querySelector('.cart-items__variants-wrapper') || details;
      props.append(a);
    }
    const TMF = /** @type {any} */ (window).TMF;
    const url = new URL(item.url || '/', window.location.origin);
    url.searchParams.set('cfg', TMF.encodeCfg(data.cfg));
    url.searchParams.set('tmf_line', item.key);
    for (const [k, v] of new URLSearchParams(this.dataset.editQuery || '')) url.searchParams.set(k, v);
    const href = url.pathname + url.search;
    if (a.getAttribute('href') !== href) a.setAttribute('href', href);
    a.setAttribute('aria-label', `Edit design of ${item.product_title}`);
    // 26/10 QA: property "Style" (design style, e.g. Vintage Color) sits under the variant option "Style: T-Shirt" ->
    // two different "Style" rows. Display only: label it "Design style" like the preview sheet (the property key is unchanged).
    for (const dt of details.querySelectorAll('.cart-items__properties dt')) {
      if (/^\s*Style\s*:?\s*$/.test(dt.textContent || '')) dt.textContent = 'Design style:';
    }
  }

  /* ---------- ONE invitation ---------- */
  #offer() {
    const items = document.querySelector('cart-drawer-component .cart-drawer__items, #cart-drawer .cart-drawer__items');
    const U = /** @type {any} */ (window).TMFUpsell;
    const phys = this.#physical();
    const count = phys.reduce((n, /** @type {any} */ i) => n + (Number(i.quantity) || 0), 0);
    const live = !!(this.#lib && this.#db && U);
    const src = live
      ? phys.find((/** @type {any} */ i) => i.properties?._cfg && this.#lineData(i)?.kind === 'apparel') || phys.find((/** @type {any} */ i) => i.properties?._cfg && this.#lineData(i))
      : null;
    /** @type {{type: string, line: any, variant?: number, price?: number} | null} */
    let offer = null;
    if (src && count > 0) {
      const d = dismissed();
      const kinds = phys.map((/** @type {any} */ i) => i.properties?._kind || kindOf(i.product_type));
      if (count < 2) {
        const md = String(this.dataset.offerSwitch || '12-07').split('-').map(Number);
        let kind = U.beforeMD(new Date(), md.length === 2 ? md : [12, 7]) ? 'ornament' : 'mug';
        if (kinds.includes(kind)) kind = kind === 'ornament' ? 'mug' : 'ornament';
        const v = Number(kind === 'ornament' ? this.dataset.ornamentVariant : this.dataset.mugVariant) || 0;
        const price = Number(kind === 'ornament' ? this.dataset.ornamentPrice : this.dataset.mugPrice) || 0;
        if (v && !d[kind]) offer = { type: kind, line: src, variant: v, price };
      }
      if (!offer && !d.bandmate && this.#lineData(src)?.kind === 'apparel') offer = { type: 'bandmate', line: src };
    }
    const sig = offer ? `${offer.type}|${offer.line.key}|${offer.line.properties._cfg}|${count}` : '';
    if (sig !== this.#boxSig) {
      this.#boxSig = sig;
      this.#box?.remove();
      this.#box = offer ? this.#buildOffer(offer, count) : null;
    }
    if (this.#box && items && (this.#box.parentElement !== items || items.lastElementChild !== this.#box)) items.append(this.#box);
  }

  /** @param {{type: string, line: any, variant?: number, price?: number}} offer @param {number} count */
  #buildOffer(offer, count) {
    const TMF = /** @type {any} */ (window).TMF, U = /** @type {any} */ (window).TMFUpsell;
    const data = this.#lineData(offer.line);
    if (!data) return null;
    const box = el('div', { cls: OFFER_CLASS, role: 'region', 'aria-label': 'One more idea' });
    const cv = /** @type {HTMLCanvasElement} */ (el('canvas', { role: 'img', 'aria-label': 'Preview with your design' }));
    const msg = el('p', { cls: 'tmf-offer__msg', role: 'status' });
    const no = el('button', { type: 'button', cls: 'tmf-link tmf-link--muted', text: 'No thanks' });
    no.addEventListener('click', () => {
      dismiss(offer.type);
      this.#boxSig = '';
      this.#offer();
    });
    if (offer.type === 'bandmate') {
      const first = String(this.dataset.tiers || '').split(',')[0]?.split(':') || [];
      const shirts = this.#physical()
        .filter((/** @type {any} */ i) => (i.properties?._kind || kindOf(i.product_type)) === 'apparel')
        .reduce((n, /** @type {any} */ i) => n + (Number(i.quantity) || 0), 0);
      const deal =
        first.length === 2 && shirts + 1 >= Number(first[0])
          ? `${first[0]} shirts save ${first[1]}%.`
          : count < 2
            ? 'Unlocks FREE US shipping.'
            : 'Same design, their own name.';
      const name = /** @type {HTMLInputElement} */ (
        el('input', { type: 'text', maxlength: '14', autocomplete: 'off', autocapitalize: 'characters', placeholder: 'Their name', 'aria-label': 'Name for the bandmate shirt' })
      );
      const add = /** @type {HTMLButtonElement} */ (el('button', { type: 'button', cls: 'tmf-btn-pill', text: this.dataset.buttonBandmate || 'Add shirt' }));
      const cfgFor = () => ({ ...data.cfg, name: String(name.value || 'MIKE').toUpperCase() });
      const draw = () => paintDesign(cv, 84, { ...data, cfg: cfgFor(), lib: this.#lib, media: this.#media, mediaBase: this.#mediaBase, onReady: () => requestAnimationFrame(draw) });
      name.addEventListener('input', () => {
        msg.textContent = '';
        name.removeAttribute('aria-invalid');
        draw();
      });
      add.addEventListener('click', async () => {
        const cfg = { ...data.cfg, name: String(name.value).trim().toUpperCase() };
        const res = TMF.validateCfg(cfg, data.design, this.#lib);
        if (!name.value.trim() || !res.ok) {
          name.setAttribute('aria-invalid', 'true');
          msg.textContent = name.value.trim()
            ? (res.errors.find((/** @type {any} */ e) => e.field === 'name') || res.errors[0] || {}).msg || 'Please check the name.'
            : 'Type their name first.';
          name.focus();
          return;
        }
        const properties = { ...TMF.summary(res.cfg, data.design, this.#lib), _cfg: JSON.stringify(res.cfg), _with: 'bandmate' };
        await this.#add(Number(offer.line.variant_id), properties, add, msg);
      });
      box.append(
        cv,
        el('div', {}, [
          el('p', { cls: 'tmf-offer__k', text: 'Shirt for a bandmate' }),
          el('p', { cls: 'tmf-offer__t', text: this.dataset.titleBandmate || 'Same design, their name' }),
          el('p', { cls: 'tmf-offer__d', text: `${offer.line.variant_title || 'Same shirt'}. ${deal}` }),
        ]),
        el('div', { cls: 'tmf-band-edit' }, [el('label', {}, ['Their name', name])]),
        el('div', { cls: 'tmf-offer__acts' }, [add, no]),
        msg
      );
      requestAnimationFrame(draw);
    } else {
      const fam = offer.type;
      const x = U.crossCfg(data.cfg, data.design, fam, { lib: this.#lib, db: this.#db, tiles: this.#media?.tiles, bgHex: this.#media?.bgHex?.[fam] });
      const res = TMF.validateCfg(x.cfg, x.design, this.#lib);
      if (!res.ok) return null;
      const add = /** @type {HTMLButtonElement} */ (el('button', { type: 'button', cls: 'tmf-btn-pill', text: `Add · ${money(offer.price || 0, this.dataset.moneyFormat)}` }));
      add.addEventListener('click', async () => {
        const properties = { ...TMF.summary(res.cfg, x.design, this.#lib), _cfg: JSON.stringify(res.cfg), _kind: fam };
        await this.#add(Number(offer.variant), properties, add, msg);
      });
      const draw = () => paintDesign(cv, 84, { kind: fam, tile: x.tile, design: x.design, cfg: res.cfg, lib: this.#lib, media: this.#media, mediaBase: this.#mediaBase, onReady: () => requestAnimationFrame(draw) });
      box.append(
        cv,
        el('div', {}, [
          el('p', { cls: 'tmf-offer__k', text: 'Unlocks FREE US shipping' }),
          el('p', { cls: 'tmf-offer__t', text: `Add the matching ${FAM_NAME[/** @type {keyof FAM_NAME} */ (fam)] || fam}` }),
          el('p', { cls: 'tmf-offer__d', text: 'Same design and name. No typing again.' }),
        ]),
        el('div', { cls: 'tmf-offer__acts' }, [add, no]),
        msg
      );
      requestAnimationFrame(draw);
    }
    return box;
  }

  /** @param {number} variantId @param {Record<string, string>} properties @param {HTMLButtonElement} button @param {HTMLElement} msg */
  async #add(variantId, properties, button, msg) {
    if (this.#busy || !variantId) return;
    this.#busy = true;
    button.disabled = true;
    try {
      await cartAdd([{ id: variantId, quantity: 1, properties }], this, 'tmf-cart-upsell');
      msg.textContent = 'Added to your cart.';
    } catch (error) {
      msg.textContent = /** @type {any} */ (error)?.message || 'Something went wrong. Please try again.';
    } finally {
      this.#busy = false;
      button.disabled = false;
    }
  }
}

const OWN = [OFFER_CLASS, THUMB_CLASS, EDIT_CLASS, 'tmf-ship', 'tmf-cart-trust'];
/** @param {MutationRecord} m */
function isOwn(m) {
  const target = m.target instanceof Element ? m.target : m.target.parentElement;
  if (target?.closest(OWN.map((c) => `.${c}`).join(','))) return true;
  if (m.removedNodes.length > 0) return false;
  const added = [...m.addedNodes];
  return added.length > 0 && added.every((n) => n instanceof Element && OWN.some((c) => n.classList.contains(c)));
}

if (!customElements.get('tmf-cart-upsell-component')) {
  customElements.define('tmf-cart-upsell-component', TmfCartUpsellComponent);
}
